import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { DEPLOY_ENV, DATADOG_APP_VERSION, API_GATEWAY_URL, SIRONA_PREVIEW } from 'config';
import { globalContext } from './constants';
import { apolloResourceBeforeSend } from './datadogApolloResource';
import { isRunningAsPWA } from './util';
import { UserAnalytics } from './analytics';
import { datadogGlobalEventFilter } from './datadogGlobalEventFilter';
import { datadogGlobalLogFilter } from './datadogGlobalLogFilter';
import useCookie from 'react-use/esm/useCookie';

export const DD_SESSION_COOKIE_KEY = '_dd_s';

type SessionCookieValue =
  | `rum=${number}&id=${string}&created=${number}&expire=${number}&logs=${number}`
  | `logs=${number}&id=${string}&created=${number}&expire=${number}`;

export const DATADOG_APPLICATION_ID = '8fbd27c5-26fd-4ef4-af77-a64666786eca';
export const DATADOG_CLIENT_TOKEN = 'pub81b4143716d6b080c751abe66b2a2d02';
export const DATADOG_SERVICE_NAME = 'srna-frontend';

export function initializeDatadog(initOptions: Partial<RumInitConfiguration> = {}) {
  /***** DataDog RUM integration *****/
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    service: DATADOG_SERVICE_NAME,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    actionNameAttribute: 'data-analytics-name',
    env: DEPLOY_ENV,
    version: DATADOG_APP_VERSION,
    allowedTracingUrls: [API_GATEWAY_URL],
    beforeSend: (event, context) => {
      if (datadogGlobalEventFilter(event, context) === false) return false;

      apolloResourceBeforeSend(event, context);

      return true;
    },
    enableExperimentalFeatures: ['feature_flags'],
    ...initOptions,
  });

  datadogRum.setGlobalContextProperty(
    globalContext.workspace.sironaStandalone,
    isRunningAsPWA() ? 'PWA' : 'Browser'
  );

  if (SIRONA_PREVIEW) {
    datadogRum.setGlobalContextProperty(globalContext.workspace.sironaPreview, SIRONA_PREVIEW);
  }

  const navEntry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  datadogRum.setGlobalContextProperty(globalContext.page.fromRefresh, navEntry?.type === 'reload');

  window.datadogRumInitialized = true;
  window.datadogRum = datadogRum;

  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['warn', 'error'],
    sessionSampleRate: 100,
    service: DATADOG_SERVICE_NAME,
    env: DEPLOY_ENV,
    version: DATADOG_APP_VERSION,
    // @ts-expect-error [EN-10263] - TS2322 - Type '(log: LogsEvent) => false | undefined' is not assignable to type '(event: LogsEvent, context: NetworkLogsEventDomainContext | ConsoleLogsEventDomainContext | LoggerLogsEventDomainContext) => boolean'.
    beforeSend: datadogGlobalLogFilter,
    sessionStor: true,
    ...initOptions,
  });
}

export function identifyUserToDataDog(user: UserAnalytics) {
  // Datadog identification
  const ddUser = datadogRum.getUser();
  // if the user is already identified in the window session for DataDog RUM, we don't want to override it
  if (ddUser.id == null || ddUser.id !== user.id) {
    datadogRum.setUser(user);
    datadogLogs.setUser(user);

    document.dispatchEvent(
      new CustomEvent('srna.user', {
        detail: {
          user,
        },
      })
    );
  }
}

/**
 * Gets a session id from the Datadog cookie (internal logic, but it doesn't seem accessible from the DD instance).
 * Note that `useCookie` does not update if the cookie is changed by other means.
 */
export function useDatadogSessionId(): string | undefined {
  const ddCookie = useCookie(DD_SESSION_COOKIE_KEY)[0] as SessionCookieValue | null;
  return ddCookie
    ?.split('&')
    .map((keyVal) => keyVal.split('='))
    .find(([key, val]) => key === 'id')?.[1];
}
